import { QueryClient } from '@tanstack/react-query';
import { ShopTypes, UmbracoTypes } from '~/lib/data-contract';
import {
    prefetchBrandEntities,
    prefetchCampaignEntities,
    prefetchFilterEntities,
    prefetchPopularEntities,
} from '~/services/loop54/hooks';
import {
    prefetchBrands,
    prefetchCategories,
    prefetchPrices,
    prefetchProduct,
} from '~/services/shop/hooks';
import {
    prefetchFilterDefaults,
    prefetchFrame,
    prefetchFrameNavigation,
    prefetchNavigation,
    prefetchPage,
    prefetchTranslations,
} from '~/services/umbraco/hooks';

export async function prefetchStaticData({ queryClient, locale, slug }: DataResolver) {
    let notFound = false;
    const asPath = '/' + (slug ?? []).join('/');

    const prefetch: Promise<(() => undefined) | undefined | unknown>[] = [
        prefetchFrame({ locale, queryClient }), // Prefetch frame data across all pages
        prefetchFrameNavigation({ locale, queryClient }), // Prefetch navigation data across all pages
        prefetchTranslations({ locale, queryClient }), // Prefetch translations across all pages];
    ];

    const page = await prefetchPage({ locale, queryClient, asPath, customerId: null }); // Page specific data

    if ('pageElements' in page) {
        // Prefetch products based on page elements
        page.pageElements?.forEach(async (block) => {
            switch (block.type) {
                case UmbracoTypes.ModuleTypes.IM30PopularProductCarouselModule: {
                    prefetch.push(prefetchPopularEntities({ queryClient }));
                    break;
                }

                case UmbracoTypes.ModuleTypes.IM30PopularProductCampaignCarouselModule: {
                    prefetch.push(
                        prefetchPopularEntities({ queryClient, onlyCampaign: true }),
                    );
                    break;
                }

                case UmbracoTypes.ModuleTypes.IM30CampaignProductCarouselModule: {
                    const { amountOfProducts, campaignID, sorting, ascendingOrder } =
                        //@ts-ignore
                        //Note: Look into this
                        block as UmbracoTypes.IM30CampaignProductCarouselModule;
                    prefetch.push(
                        prefetchCampaignEntities({
                            queryClient,
                            amountOfProducts,
                            campaignID,
                            sorting,
                            ascendingOrder,
                        }),
                    );
                    break;
                }

                case UmbracoTypes.ModuleTypes.IM30BrandProductCarouselModule: {
                    //@ts-ignore
                    //Note: Look into this
                    const { brand } = block as UmbracoTypes.IM30BrandProductCarouselModule;
                    prefetch.push(prefetchBrandEntities({ queryClient, brand }));
                    break;
                }

                case UmbracoTypes.ModuleTypes.IM30SpecificProductNumbersCarouselModule:
                // case UmbracoTypes.ModuleTypes.IM77SpecificProductNumbersGridModule: {
                //     const { productNumbers } =
                //         block as unknown as UmbracoTypes.IM30SpecificProductNumbersCarouselModule;
                //     prefetch.push(
                //         prefetchSKUEntities({
                //             queryClient,
                //             productNumbers,
                //             isMobile: false,
                //         }),
                //     );
                //     break;
                // }

                default:
                    break;
            }
        });
    }

    // Prefetch data based on the page type
    switch (page?.type) {
        case UmbracoTypes.PageTypes.IP10SearchPage: {
            prefetch.push(prefetchFilterDefaults({ queryClient }));
            break;
        }

        case UmbracoTypes.PageTypes.IP11PopularProductsPage: {
            prefetch.push(prefetchPopularEntities({ queryClient, amount: 60 }));
            break;
        }

        case UmbracoTypes.PageTypes.IP14BrandOverviewPage: {
            prefetch.push(prefetchCategories({ queryClient }));
            prefetch.push(prefetchBrands({ queryClient, brandCategory: '', query: '' }));
            break;
        }

        case UmbracoTypes.PageTypes.IP31CategoryListPage: {
            prefetch.push(prefetchNavigation({ locale, slug, queryClient }));
            break;
        }

        case UmbracoTypes.PageTypes.IP40ProductListPage:
        case UmbracoTypes.PageTypes.IP41CategoryNode: {
            const filterDefaults = await prefetchFilterDefaults({ queryClient });
            prefetch.push(
                prefetchNavigation({ locale, slug, queryClient }),
                prefetchFilterEntities({
                    breadcrumb: page?.breadcrumb,
                    filterAdmin: page?.pageProperties?.filterAdmin,
                    filterDefaults,
                    pageTitle: page?.title,
                    queryClient,
                }),
            );
            break;
        }

        case UmbracoTypes.PageTypes.IP50ProductDetailPage: {
            const itemNo = decodeURIComponent(page?.pageProperties?.productItemNumber || '');
            const { product, errorType } = await prefetchProduct({ itemNo, queryClient });

            if (errorType === 'ProductItemNotFoundException') {
                notFound = true;
                break;
            }

            // Related/complementary Loop54 results use cacheTime: 0 (personalized per session);
            // client refetches on mount, so SSR prefetch only bloats dehydratedState.
            const productIsObsolete =
                product?.item?.itemStatus === ShopTypes.ItemStatusValues.Obsolete;

            if (!productIsObsolete) {
                prefetch.push(
                    prefetchPrices({
                        ids: [itemNo],
                        queryClient,
                    }),
                );
            }

            break;
        }

        case UmbracoTypes.PageTypes.IP140NotFoundPage: {
            notFound = true;
            break;
        }

        case UmbracoTypes.PageTypes.IP150ErrorPage: {
            // If there is an error from page endpoint we manually throw an error, the last successfully generated page will then continue to show
            if (slug?.[0] !== '500') {
                throw new Error(`Failed to fetch page "${asPath}"`);
            }
            break;
        }

        default:
            break;
    }

    if ((page as UmbracoTypes.IPages)?.statusCode === 404) {
        notFound = true;
    }

    // Wait for all prefetch to complete
    await Promise.all(prefetch);

    return { page, notFound };
}

interface DataResolver {
    queryClient: QueryClient;
    locale: string | undefined;
    slug: string[] | undefined;
}
